import React, { useEffect, useState } from "react";
import API, { CONFIG, UPDATE_CONFIG, UPDATE_WORD_BONUS, UPDATE_LETTER_RATES } from "../../network/api";
import { TEXT_DEF } from "../../constant/text";
import { Button, InputNumber, Input, message } from "antd";
import ModalCommon from "../../components/Modal";

const PHRASE_DEFS = [
  { key: "DOANVIEN", label: "ĐOÀN VIÊN", aliases: ["ĐOÀN VIÊN", "DOANVIEN", "DOAN_VIEN", "DOAN VIEN"], defaultScore: 888 },
  { key: "TRANGRAM", label: "TRĂNG RẰM", aliases: ["TRĂNG RẰM", "TRANGRAM", "TRANG_RAM", "TRANG RAM"], defaultScore: 688 },
  { key: "TRANGVANG", label: "TRĂNG VÀNG", aliases: ["TRĂNG VÀNG", "TRANGVANG", "TRANG_VANG", "TRANG VANG"], defaultScore: 588 },
  { key: "PHUCLOC", label: "PHÚC LỘC", aliases: ["PHÚC LỘC", "PHUCLOC", "PHUC_LOC", "PHUC LOC"], defaultScore: 488 },
  { key: "RUOCDEN", label: "RƯỚC ĐÈN", aliases: ["RƯỚC ĐÈN", "RUOCDEN", "RUOC_DEN", "RUOC DEN"], defaultScore: 388 },
  { key: "PHACO", label: "PHÁ CỖ", aliases: ["PHÁ CỖ", "PHACO", "PHA_CO", "PHA CO"], defaultScore: 288 },
];

const LETTER_DEFS = [
  { key: "A", defaultRate: 12 },
  { key: "N", defaultRate: 12 },
  { key: "O", defaultRate: 10 },
  { key: "R", defaultRate: 9 },
  { key: "C", defaultRate: 8 },
  { key: "G", defaultRate: 7 },
  { key: "H", defaultRate: 6 },
  { key: "U", defaultRate: 6 },
  { key: "D", defaultRate: 5 },
  { key: "E", defaultRate: 5 },
  { key: "T", defaultRate: 5 },
  { key: "P", defaultRate: 4 },
  { key: "V", defaultRate: 3 },
  { key: "I", defaultRate: 3 },
  { key: "L", defaultRate: 3 },
  { key: "M", defaultRate: 2 },
];

const toPercent = (raw, fallback = 0) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return n <= 1 ? Math.round(n * 10000) / 100 : n;
};

const toDecimalRate = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 0;
  return Number((n / 100).toFixed(4));
};

const roundPct = (n) => Math.round((Number(n) || 0) * 100) / 100;

const sumRates = (hit, box) => roundPct(roundPct(hit) + roundPct(box));

const vipRateOverflowError = (hit, box) => {
  const total = sumRates(hit, box);
  if (total > 100) {
    return `${roundPct(hit)}% điểm thưởng + ${roundPct(box)}% hộp chữ ngọc = ${total}%. Tổng đang vượt 100%, vui lòng giảm một trong hai ô.`;
  }
  return null;
};

const vipRateSumHint = (hit, box) => {
  const overflow = vipRateOverflowError(hit, box);
  if (overflow) return overflow;
  const remain = roundPct(100 - sumRates(hit, box));
  if (Math.abs(remain) < 0.01) return null;
  return `Còn lại ${remain}% tỉ lệ không có điểm (100% - ${roundPct(hit)}% điểm thưởng - ${roundPct(box)}% hộp chữ ngọc).`;
};

const sumLetterRates = (rates) =>
  roundPct(LETTER_DEFS.reduce((sum, l) => sum + roundPct(rates?.[l.key]), 0));

const letterRateSumError = (rates) => {
  const total = sumLetterRates(rates);
  if (total > 100) return `Tổng tỷ lệ nhận chữ ${total}% vượt 100%.`;
  if (Math.abs(total - 100) > 0.01) return `Tổng tỷ lệ nhận chữ phải bằng 100% (hiện ${total}%).`;
  return null;
};

const pickFirstObject = (...objs) =>
  objs.find((o) => o && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length) || {};

const readAliasedNumber = (src, aliases, fallback) => {
  for (const alias of aliases) {
    if (src?.[alias] != null && Number.isFinite(Number(src[alias]))) return Number(src[alias]);
  }
  return fallback;
};

const tableHeader = { background: "#1890ff", color: "#fff", padding: "10px 12px", textAlign: "left" };
const tableCell = { padding: "8px 12px", borderBottom: "1px solid #f0f0f0", background: "#fff" };

const SetupScore = () => {
  const [configGame, setConfigGame] = useState();
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [isShowModalSuccess, setIsShowModalSuccess] = useState(false);

  // MAX_SCORE
  const [vip0_5, setVip0_5] = useState(0);
  const [vip6_11, setVip6_11] = useState(0);
  const [vip12_15, setVip12_15] = useState(0);
  const [vip16_20, setVip16_20] = useState(0);

  // HIT_RATE (%)
  const [rwd0_5, setRwd0_5] = useState(0);
  const [rwd6_11, setRwd6_11] = useState(0);
  const [rwd12_15, setRwd12_15] = useState(0);
  const [rwd16_20, setRwd16_20] = useState(0);

  // Tỉ lệ % hộp chữ ngọc
  const [box0_5, setBox0_5] = useState(0);
  const [box6_11, setBox6_11] = useState(0);
  const [box12_15, setBox12_15] = useState(0);
  const [box16_20, setBox16_20] = useState(0);

  // Ranking rewards
  const [top1, setTop1] = useState();
  const [top2, setTop2] = useState();
  const [top3, setTop3] = useState();
  const [top4, setTop4] = useState();
  const [top5, setTop5] = useState();
  const [top6, setTop6] = useState();
  const [top7, setTop7] = useState();
  const [top8, setTop8] = useState();
  const [top9, setTop9] = useState();
  const [top10, setTop10] = useState();
  const [preWithdrawRounds, setPreWithdrawRounds] = useState(8);

  // Jackpot (chuỗi có dấu gạch ngang, PATCH là mảng số)
  const [jpVip0_5Str, setJpVip0_5Str] = useState("");
  const [jpVip6_11Str, setJpVip6_11Str] = useState("");
  const [jpVip12_15Str, setJpVip12_15Str] = useState("");
  const [jpVip16_20Str, setJpVip16_20Str] = useState("");

  const [phraseScores, setPhraseScores] = useState(() =>
    Object.fromEntries(PHRASE_DEFS.map((p) => [p.key, p.defaultScore]))
  );
  const [letterRates, setLetterRates] = useState(() =>
    Object.fromEntries(LETTER_DEFS.map((l) => [l.key, l.defaultRate]))
  );

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    getDataConfig();
  }, []);

  // Helpers: string <-> number[]
  const arrToDashString = (arr) =>
    Array.isArray(arr) ? arr.join(" - ") : "";

  const dashStringToNumArr = (s) => {
    if (!s?.trim()) return [];
    return s
      .split(/[-,]/g)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => parseInt(x, 10))
      .filter((n) => Number.isFinite(n) && n >= 0);
  };

  const isValidPoints = (s) => {
    if (!s?.trim()) return false;
    const parts = s
      .split(/[-,]/g)
      .map((x) => x.trim())
      .filter(Boolean);
    if (parts.length === 0) return false;
    return parts.every((p) => {
      const n = parseInt(p, 10);
      return Number.isFinite(n) && n >= 0;
    });
  };

  // Validate
  const validNonNegativeNumber = (v) => {
    if (v === undefined || v === null || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0;
  };

  const validPositiveNumber = (v) => {
    if (v === undefined || v === null || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  };

  const validPercent = (v) => {
    if (v === undefined || v === null || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 && n <= 100;
  };

  // Load config
  const getDataConfig = async () => {
    try {
      const res = await API.get(`${CONFIG}`);
      const data = res.data.data;

      setConfigGame({
        vip0_5: data?.vipLevels?.["VIP_0_5"] ?? {},
        vip6_11: data?.vipLevels?.["VIP_6_11"] ?? {},
        vip12_15: data?.vipLevels?.["VIP_12_15"] ?? {},
        vip16_20: data?.vipLevels?.["VIP_16_20"] ?? {},
        top1: data?.eventRewards?.["TOP1"],
        top2: data?.eventRewards?.["TOP2"],
        top3: data?.eventRewards?.["TOP3"],
        top4: data?.eventRewards?.["TOP4"],
        top5: data?.eventRewards?.["TOP5"],
        top6: data?.eventRewards?.["TOP6"],
        top7: data?.eventRewards?.["TOP7"],
        top8: data?.eventRewards?.["TOP8"],
        top9: data?.eventRewards?.["TOP9"],
        top10: data?.eventRewards?.["TOP10"],
        pointsJackpot: data?.pointsJackpot ?? {},
      });

      // MAX_SCORE
      setVip0_5(data?.vipLevels?.["VIP_0_5"]?.MAX_SCORE ?? 0);
      setVip6_11(data?.vipLevels?.["VIP_6_11"]?.MAX_SCORE ?? 0);
      setVip12_15(data?.vipLevels?.["VIP_12_15"]?.MAX_SCORE ?? 0);
      setVip16_20(data?.vipLevels?.["VIP_16_20"]?.MAX_SCORE ?? 0);

      // HIT_RATE (BE thập phân 0.8 = 80%)
      const hit0 = toPercent(data?.vipLevels?.["VIP_0_5"]?.HIT_RATE, 0);
      const hit1 = toPercent(data?.vipLevels?.["VIP_6_11"]?.HIT_RATE, 0);
      const hit2 = toPercent(data?.vipLevels?.["VIP_12_15"]?.HIT_RATE, 0);
      const hit3 = toPercent(data?.vipLevels?.["VIP_16_20"]?.HIT_RATE, 0);
      setRwd0_5(hit0);
      setRwd6_11(hit1);
      setRwd12_15(hit2);
      setRwd16_20(hit3);
      setBox0_5(toPercent(data?.vipLevels?.["VIP_0_5"]?.JADE_BOX_RATE, 0));
      setBox6_11(toPercent(data?.vipLevels?.["VIP_6_11"]?.JADE_BOX_RATE, 0));
      setBox12_15(toPercent(data?.vipLevels?.["VIP_12_15"]?.JADE_BOX_RATE, 0));
      setBox16_20(toPercent(data?.vipLevels?.["VIP_16_20"]?.JADE_BOX_RATE, 0));

      // Ranking rewards
      setTop1(data?.eventRewards?.["TOP1"]);
      setTop2(data?.eventRewards?.["TOP2"]);
      setTop3(data?.eventRewards?.["TOP3"]);
      setTop4(data?.eventRewards?.["TOP4"]);
      setTop5(data?.eventRewards?.["TOP5"]);
      setTop6(data?.eventRewards?.["TOP6"]);
      setTop7(data?.eventRewards?.["TOP7"]);
      setTop8(data?.eventRewards?.["TOP8"]);
      setTop9(data?.eventRewards?.["TOP9"]);
      setTop10(data?.eventRewards?.["TOP10"]);
      setPreWithdrawRounds(
        Number(
          data?.multiple ??
            data?.preWithdrawRounds ??
            data?.gameplay?.preWithdrawRounds ??
            8
        ) || 8
      );

      // Jackpot strings
      setJpVip0_5Str(arrToDashString(data?.pointsJackpot?.VIP_0_5));
      setJpVip6_11Str(arrToDashString(data?.pointsJackpot?.VIP_6_11));
      setJpVip12_15Str(arrToDashString(data?.pointsJackpot?.VIP_12_15));
      setJpVip16_20Str(arrToDashString(data?.pointsJackpot?.VIP_16_20));

      const phraseSrc = pickFirstObject(
        data?.wordBonusScores,
        data?.wordRewards,
        data?.combinationRewards,
        data?.phraseScores,
        data?.phraseRewards
      );
      setPhraseScores(
        Object.fromEntries(
          PHRASE_DEFS.map((p) => [
            p.key,
            readAliasedNumber(phraseSrc, [p.key, ...p.aliases], p.defaultScore),
          ])
        )
      );

      const letterSrc = pickFirstObject(
        data?.letterRates,
        data?.charRates,
        data?.letterDropRates,
        data?.characterRates
      );
      setLetterRates(
        Object.fromEntries(
          LETTER_DEFS.map((l) => [
            l.key,
            readAliasedNumber(letterSrc, [l.key, l.key.toLowerCase()], l.defaultRate),
          ])
        )
      );
    } catch (err) {
      console.log(err);
    }
  };

  // Common handlers (ranking + jackpot)
  const handleChangeInput = (e, key) => {
    switch (key) {
      case 5: setTop1(e); return;
      case 6: setTop2(e); return;
      case 7: setTop3(e); return;
      case 8: setTop4(e); return;
      case 9: setTop5(e); return;
      case 10: setTop6(e); return;
      case 11: setTop7(e); return;
      case 12: setTop8(e); return;
      case 13: setTop9(e); return;
      case 14: setTop10(e); return;
      case 15: setJpVip0_5Str(e); return;
      case 16: setJpVip6_11Str(e); return;
      case 17: setJpVip12_15Str(e); return;
      case 18: setJpVip16_20Str(e); return;
      default: return;
    }
  };

  const handleUpdateConfig = async () => {
    try {
      setIsShowModalConfirm(false);

      // Validate MAX_SCORE (>=0) + HIT_RATE (0..100)
      if (
        !validNonNegativeNumber(vip0_5) ||
        !validNonNegativeNumber(vip6_11) ||
        !validNonNegativeNumber(vip12_15) ||
        !validNonNegativeNumber(vip16_20) ||
        !validPercent(rwd0_5) ||
        !validPercent(rwd6_11) ||
        !validPercent(rwd12_15) ||
        !validPercent(rwd16_20) ||
        !validPercent(box0_5) ||
        !validPercent(box6_11) ||
        !validPercent(box12_15) ||
        !validPercent(box16_20)
      ) {
        showError("Vui lòng nhập hợp lệ (MAX_SCORE ≥ 0, các tỉ lệ 0–100%).");
        return;
      }

      const vipRateRows = [
        { label: "VIP 0 - 5", hit: rwd0_5, box: box0_5 },
        { label: "VIP 6 - 11", hit: rwd6_11, box: box6_11 },
        { label: "VIP 12 - 15", hit: rwd12_15, box: box12_15 },
        { label: "VIP 16 - 20", hit: rwd16_20, box: box16_20 },
      ];
      for (const row of vipRateRows) {
        const overflow = vipRateOverflowError(row.hit, row.box);
        if (overflow) {
          showError(`${row.label}: ${overflow}`);
          return;
        }
      }

      // Validate ranking ( > 0 )
      if (
        !validPositiveNumber(top1) || !validPositiveNumber(top2) || !validPositiveNumber(top3) ||
        !validPositiveNumber(top4) || !validPositiveNumber(top5) || !validPositiveNumber(top6) ||
        !validPositiveNumber(top7) || !validPositiveNumber(top8) || !validPositiveNumber(top9) ||
        !validPositiveNumber(top10)
      ) {
        showError("Bảng xếp hạng phải là số dương (>0).");
        return;
      }
      if (!validNonNegativeNumber(preWithdrawRounds)) {
        showError("Vòng cược trước khi rút phải ≥ 0.");
        return;
      }

      // Validate jackpot strings
      if (
        !isValidPoints(jpVip0_5Str) ||
        !isValidPoints(jpVip6_11Str) ||
        !isValidPoints(jpVip12_15Str) ||
        !isValidPoints(jpVip16_20Str)
      ) {
        showError("Điểm jackpot không hợp lệ. Ví dụ: 8 - 28 - 38 - 58.");
        return;
      }

      if (PHRASE_DEFS.some((p) => !validNonNegativeNumber(phraseScores[p.key]))) {
        showError("Điểm thưởng cụm từ phải ≥ 0.");
        return;
      }
      if (LETTER_DEFS.some((l) => !validPercent(letterRates[l.key]))) {
        showError("Tỷ lệ nhận chữ phải trong khoảng 0–100%.");
        return;
      }
      const letterSumErr = letterRateSumError(letterRates);
      if (letterSumErr) {
        showError(letterSumErr);
        return;
      }

      setIsShowModalSuccess(false);

      const body = {
        config: {
          vipLevels: {
            VIP_0_5: {
              ...(configGame?.vip0_5 ?? {}),
              MAX_SCORE: Number(vip0_5 ?? 0),
              HIT_RATE: toDecimalRate(rwd0_5),
              JADE_BOX_RATE: toDecimalRate(box0_5),
            },
            VIP_6_11: {
              ...(configGame?.vip6_11 ?? {}),
              MAX_SCORE: Number(vip6_11 ?? 0),
              HIT_RATE: toDecimalRate(rwd6_11),
              JADE_BOX_RATE: toDecimalRate(box6_11),
            },
            VIP_12_15: {
              ...(configGame?.vip12_15 ?? {}),
              MAX_SCORE: Number(vip12_15 ?? 0),
              HIT_RATE: toDecimalRate(rwd12_15),
              JADE_BOX_RATE: toDecimalRate(box12_15),
            },
            VIP_16_20: {
              ...(configGame?.vip16_20 ?? {}),
              MAX_SCORE: Number(vip16_20 ?? 0),
              HIT_RATE: toDecimalRate(rwd16_20),
              JADE_BOX_RATE: toDecimalRate(box16_20),
            },
          },
          eventRewards: {
            TOP1: Number(top1),
            TOP2: Number(top2),
            TOP3: Number(top3),
            TOP4: Number(top4),
            TOP5: Number(top5),
            TOP6: Number(top6),
            TOP7: Number(top7),
            TOP8: Number(top8),
            TOP9: Number(top9),
            TOP10: Number(top10),
          },
          pointsJackpot: {
            VIP_0_5: dashStringToNumArr(jpVip0_5Str),
            VIP_6_11: dashStringToNumArr(jpVip6_11Str),
            VIP_12_15: dashStringToNumArr(jpVip12_15Str),
            VIP_16_20: dashStringToNumArr(jpVip16_20Str),
          },
          multiple: Number(preWithdrawRounds ?? 0),
        },
      };

      const wordBonusScores = Object.fromEntries(
        PHRASE_DEFS.map((p) => [p.key, Number(phraseScores[p.key] ?? p.defaultScore)])
      );
      const letterRatesBody = Object.fromEntries(
        LETTER_DEFS.map((l) => [l.key, Number(letterRates[l.key] ?? l.defaultRate)])
      );

      await API.patch(`${UPDATE_CONFIG}`, body);
      await API.patch(UPDATE_WORD_BONUS, { wordBonusScores });
      await API.patch(UPDATE_LETTER_RATES, { letterRates: letterRatesBody });
      setIsShowModalSuccess(true);
    } catch (err) {
      console.log(err);
      showError(err?.data?.message || err?.response?.data?.message || "Cập nhật thất bại.");
    }
  };

  const showError = (errorMessage) => {
    messageApi.open({
      type: "error",
      content: errorMessage,
    });
  };

  const handleShowModal = () => {
    const vipRateRows = [
      { label: "VIP 0 - 5", hit: rwd0_5, box: box0_5 },
      { label: "VIP 6 - 11", hit: rwd6_11, box: box6_11 },
      { label: "VIP 12 - 15", hit: rwd12_15, box: box12_15 },
      { label: "VIP 16 - 20", hit: rwd16_20, box: box16_20 },
    ];
    for (const row of vipRateRows) {
      const overflow = vipRateOverflowError(row.hit, row.box);
      if (overflow) {
        showError(`${row.label}: ${overflow}`);
        return;
      }
    }
    const letterSumErr = letterRateSumError(letterRates);
    if (letterSumErr) {
      showError(letterSumErr);
      return;
    }
    setIsShowModalConfirm(true);
  };

  const letterTotal = sumLetterRates(letterRates);
  const letterOver = letterTotal > 100;
  const letterNot100 = Math.abs(letterTotal - 100) > 0.01;
  const letterStatus = letterOver ? "error" : letterNot100 ? "warning" : undefined;

  const renderVipRateRow = (label, maxVal, setMax, hit, setHit, box, setBox) => {
    const total = sumRates(hit, box);
    const over = total > 100;
    const hint = vipRateSumHint(hit, box);
    const status = over ? "error" : hint ? "warning" : undefined;
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr", gap: "12px", alignItems: "center" }}>
          <span style={{ textAlign: "right" }}>{label}:</span>
          <InputNumber style={{ width: "100%" }} value={maxVal} onChange={(v) => setMax(Number(v ?? 0))} max={500} min={0} />
          <InputNumber style={{ width: "100%" }} status={status} value={hit} onChange={(v) => setHit(Number(v ?? 0))} max={100} min={0} addonAfter="%" />
          <InputNumber style={{ width: "100%" }} status={status} value={box} onChange={(v) => setBox(Number(v ?? 0))} max={100} min={0} addonAfter="%" />
        </div>
        {hint && (
          <div style={{ color: over ? "#ff4d4f" : "#d48806", fontSize: 12, marginLeft: 152, marginTop: 4 }}>
            {hint}
          </div>
        )}
      </div>
    );
  };

  // Reusable row (InputNumber hoặc Input text)
  const renderInputNumer = (
    key,
    defaultValue,
    changeInput,
    max,
    label,
    isText = false
  ) => {
    return (
      <div className={`flex w-full items-center ${key !== "vip0_5" ? "mt-4" : ""}`}>
        <div className="flex w-[20%] justify-end mr-10">{label}:</div>
        <div className="w-[60%]">
          {isText ? (
            <Input
              key={key}
              style={{ width: "100%" }}
              value={defaultValue ?? ""}
              placeholder="Ví dụ: 8 - 28 - 38"
              onChange={(e) => changeInput(e.target.value)}
              onBlur={(e) => {
                const arr = dashStringToNumArr(e.target.value);
                changeInput(arrToDashString(arr));
              }}
            />
          ) : (
            <InputNumber
              key={key}
              style={{ width: "100%" }}
              value={defaultValue}
              onChange={changeInput}
              max={max}
              min={0} // cho phép 0 để không bị đỏ khi API trả 0
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="col-12" style={{ background: "#f7f9fa", minHeight: "100vh", padding: 24 }}>
      {contextHolder}
      <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Cài đặt điểm nhận</div>

      <div style={{ background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 2px 8px #eee", marginBottom: 32 }}>
        {/* VIP */}
        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Cấp VIP và tỉ lệ trả thưởng</div>
        <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
          <div style={{ width: "100%" }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr", gap: "12px", marginBottom: 8 }}>
              <div></div>
              <div style={{ textAlign: "center", fontWeight: 400 }}>Điểm nhận cao nhất trong 1 lượt</div>
              <div style={{ textAlign: "center", fontWeight: 400 }}>Tỉ lệ % điểm thưởng</div>
              <div style={{ textAlign: "center", fontWeight: 400 }}>Tỉ lệ % hộp chữ ngọc</div>
            </div>

            {/* Rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {renderVipRateRow("VIP 0 - 5", vip0_5, setVip0_5, rwd0_5, setRwd0_5, box0_5, setBox0_5)}
              {renderVipRateRow("VIP 6 - 11", vip6_11, setVip6_11, rwd6_11, setRwd6_11, box6_11, setBox6_11)}
              {renderVipRateRow("VIP 12 - 15", vip12_15, setVip12_15, rwd12_15, setRwd12_15, box12_15, setBox12_15)}
              {renderVipRateRow("VIP 16 - 20", vip16_20, setVip16_20, rwd16_20, setRwd16_20, box16_20, setBox16_20)}
            </div>
          </div>
        </div>

        {renderInputNumer(
          "preWithdrawRounds",
          preWithdrawRounds,
          (e) => setPreWithdrawRounds(Number(e ?? 0)),
          999,
          "Vòng cược trước khi rút"
        )}

        {/* Ranking */}
        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Bảng xếp hạng</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          {renderInputNumer("top1", top1, (e) => handleChangeInput(e, 5), 999, "Top 1")}
          {renderInputNumer("top2", top2, (e) => handleChangeInput(e, 6), 999, "Top 2")}
          {renderInputNumer("top3", top3, (e) => handleChangeInput(e, 7), 999, "Top 3")}
          {renderInputNumer("top4", top4, (e) => handleChangeInput(e, 8), 999, "Top 4")}
          {renderInputNumer("top5", top5, (e) => handleChangeInput(e, 9), 999, "Top 5")}
          {renderInputNumer("top6", top6, (e) => handleChangeInput(e, 10), 999, "Top 6")}
          {renderInputNumer("top7", top7, (e) => handleChangeInput(e, 11), 999, "Top 7")}
          {renderInputNumer("top8", top8, (e) => handleChangeInput(e, 12), 999, "Top 8")}
          {renderInputNumer("top9", top9, (e) => handleChangeInput(e, 13), 999, "Top 9")}
          {renderInputNumer("top10", top10, (e) => handleChangeInput(e, 14), 999, "Top 10")}
        </div>

        {/* Jackpot */}
        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Số điểm nhận chị hằng</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          {renderInputNumer("jp_vip0_5", jpVip0_5Str, (v) => handleChangeInput(v, 15), undefined, "VIP 0 - 5", true)}
          {renderInputNumer("jp_vip6_11", jpVip6_11Str, (v) => handleChangeInput(v, 16), undefined, "VIP 6 - 11", true)}
          {renderInputNumer("jp_vip12_15", jpVip12_15Str, (v) => handleChangeInput(v, 17), undefined, "VIP 12 - 15", true)}
          {renderInputNumer("jp_vip16_20", jpVip16_20Str, (v) => handleChangeInput(v, 18), undefined, "VIP 16 - 20", true)}
        </div>

        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Số điểm nhận của cụm từ</div>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32 }}>
          <thead>
            <tr>
              <th style={tableHeader}>Bộ chữ</th>
              <th style={tableHeader}>Điểm thưởng</th>
            </tr>
          </thead>
          <tbody>
            {PHRASE_DEFS.map((p) => (
              <tr key={p.key}>
                <td style={{ ...tableCell, fontWeight: 600 }}>{p.label}</td>
                <td style={tableCell}>
                  <InputNumber
                    style={{ width: 180 }}
                    min={0}
                    value={phraseScores[p.key]}
                    onChange={(v) => setPhraseScores((s) => ({ ...s, [p.key]: Number(v ?? 0) }))}
                    addonAfter="điểm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Cấu hình tỷ lệ nhận chữ</div>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: letterNot100 ? 8 : 24 }}>
          <thead>
            <tr>
              <th style={{ ...tableHeader, width: 80 }}>STT</th>
              <th style={tableHeader}>Chữ cái</th>
              <th style={tableHeader}>Tỷ lệ nhận</th>
            </tr>
          </thead>
          <tbody>
            {LETTER_DEFS.map((l, idx) => (
              <tr key={l.key}>
                <td style={tableCell}>{idx + 1}</td>
                <td style={{ ...tableCell, fontWeight: 600 }}>{l.key}</td>
                <td style={tableCell}>
                  <InputNumber
                    style={{ width: 160 }}
                    status={letterStatus}
                    min={0}
                    max={100}
                    step={0.1}
                    precision={2}
                    value={letterRates[l.key]}
                    onChange={(v) => setLetterRates((s) => ({ ...s, [l.key]: Number(v ?? 0) }))}
                    addonAfter="%"
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ ...tableCell, fontWeight: 600 }} colSpan={2}>Tổng</td>
              <td style={{ ...tableCell, fontWeight: 600, color: letterOver ? "#ff4d4f" : letterNot100 ? "#d48806" : undefined }}>
                {letterTotal}%
              </td>
            </tr>
          </tbody>
        </table>
        {letterNot100 && (
          <div style={{ color: letterOver ? "#ff4d4f" : "#d48806", fontSize: 12, marginBottom: 24 }}>
            {letterOver
              ? `Tổng tỷ lệ nhận chữ ${letterTotal}% vượt 100%.`
              : `Tổng tỷ lệ nhận chữ phải bằng 100% (hiện ${letterTotal}%).`}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button type="primary" size="large" style={{ minWidth: 120 }} onClick={handleShowModal}>
            Cập nhật
          </Button>
        </div>
      </div>

      {/* Confirm */}
      {isShowModalConfirm && (
        <ModalCommon
          isShow={isShowModalConfirm}
          message={TEXT_DEF.text0062}
          txtBtnAcc={TEXT_DEF.text0008}
          textBtnClose={TEXT_DEF.text0023}
          actionNext={handleUpdateConfig}
          close={() => setIsShowModalConfirm(false)}
        />
      )}

      {/* Success */}
      {isShowModalSuccess && (
        <ModalCommon
          isShow={isShowModalSuccess}
          message={TEXT_DEF.text0063}
          txtBtnAcc={TEXT_DEF.text0007}
          actionNext={() => setIsShowModalSuccess(false)}
          close={() => setIsShowModalSuccess(false)}
        />
      )}
    </div>
  );
};

export default SetupScore;
